import { HttpClient } from '@angular/common/http';
import { inject, Injectable, OnDestroy, signal } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { z } from 'zod';
import {
  QuoteRevisionItem,
  QuoteRevisionItemWrite,
  QuoteRevisionItemsMutationResponse,
  QuoteRevisionItemsResponse,
} from '../../models/quote-revision-item.model';
import { QuoteService } from '../quote.service';
import { DownloadService } from './download.service';
import { RealtimeSocketService } from './realtime/realtime-socket.service';
import { DFMCheckStatus } from '../../models/dfm-check.model';

@Injectable()
export class QuoteRevisionItemService implements OnDestroy {
  private http = inject(HttpClient);
  private downloadService = inject(DownloadService);
  private quoteService = inject(QuoteService);
  private realtimeSocket = inject(RealtimeSocketService);
  private loadSequence = 0;
  private quoteId?: string;
  private revisionNumber?: number;
  private dfmSocketSubscription?: Subscription;
  private pricingSocketSubscription?: Subscription;
  readonly dfmRerunning = signal(false);
  /** True while a different revision's items are being fetched. */
  readonly loading = signal(false);

  private itemsSubject = new BehaviorSubject<QuoteRevisionItem[] | undefined>(
    undefined,
  );
  readonly items$ = this.itemsSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  readonly error$ = this.errorSubject.asObservable();

  private draggingSubject = new BehaviorSubject<QuoteRevisionItem | false>(
    false,
  );
  readonly dragging$ = this.draggingSubject.asObservable();

  async getQuoteRevisionItems(
    quoteId: string,
    revisionNumber: number,
  ): Promise<QuoteRevisionItem[]> {
    const loadSequence = ++this.loadSequence;
    const quoteChanged = this.quoteId !== quoteId;
    const contextChanged =
      quoteChanged || this.revisionNumber !== revisionNumber;
    this.quoteId = quoteId;
    this.revisionNumber = revisionNumber;
    // Only a different quote clears the tree. Switching revisions (or
    // refreshing the same one) keeps the rendered rows until the replacement
    // response arrives, so the list does not collapse and re-expand.
    if (quoteChanged) {
      this.itemsSubject.next(undefined);
    }
    if (contextChanged) {
      this.connectDfmSocket();
      this.loading.set(true);
    }
    this.errorSubject.next(null);

    try {
      const response = await firstValueFrom(
        this.http.get<unknown>(
          `/quotes-api/quote-revision-items/${quoteId}/${revisionNumber}/`,
        ),
      );
      const items = QuoteRevisionItemsResponse.parse(response);
      if (loadSequence === this.loadSequence) {
        this.itemsSubject.next(items);
      }
      return items;
    } catch (error) {
      if (loadSequence === this.loadSequence) {
        this.itemsSubject.next([]);
        this.errorSubject.next('The revision items could not be loaded.');
      }
      throw error;
    } finally {
      if (loadSequence === this.loadSequence) {
        this.loading.set(false);
      }
    }
  }

  ngOnDestroy(): void {
    this.disconnectDfmSocket();
  }

  get draggingItem(): QuoteRevisionItem | false {
    return this.draggingSubject.value;
  }

  dragStart(item: QuoteRevisionItem): void {
    this.draggingSubject.next(item);
  }

  dragEnd(): void {
    this.draggingSubject.next(false);
  }

  async downloadItemCad(persistentId: string): Promise<void> {
    await this.downloadService.downloadFile(
      `${this.revisionItemsUrl()}${persistentId}/download-cad/`,
      {
        defaultFilename: 'Quote-Item-CAD.zip',
        mimeType: 'application/zip',
      },
    );
  }

  async downloadQuoteCad(): Promise<void> {
    await this.downloadService.downloadFile(
      `${this.revisionItemsUrl()}download-cad/`,
      {
        defaultFilename: 'Quote-CAD.zip',
        mimeType: 'application/zip',
      },
    );
  }

  async downloadAllDrawings(): Promise<void> {
    await this.downloadService.downloadFile(
      `${this.revisionItemsUrl()}download-drawings/`,
      {
        defaultFilename: 'All-Drawing-Files.zip',
        mimeType: 'application/zip',
      },
    );
  }

  async downloadAllBlockingUp(): Promise<void> {
    await this.downloadService.downloadFile(
      `${this.revisionItemsUrl()}download-blocking-up/`,
      {
        defaultFilename: 'All-Blocking-Up.zip',
        mimeType: 'application/zip',
      },
    );
  }

  async downloadCSV(): Promise<void> {
    const url = `${this.revisionItemsUrl()}download-csv/`;
    await this.downloadService.downloadFile(url, {
      defaultFilename: `${this.quoteService.quote.code_number}-${this.revisionNumber}.csv`,
      mimeType: 'text/csv',
    });
  }

  async bulkCreate(
    items: QuoteRevisionItemWrite[],
  ): Promise<QuoteRevisionItem[]> {
    return this.mutate('post', { items });
  }

  async bulkUpdate(
    items: QuoteRevisionItemWrite[],
  ): Promise<QuoteRevisionItem[]> {
    return this.mutate('patch', { items });
  }

  async updateItem(
    persistentId: string,
    patch: Omit<QuoteRevisionItemWrite, 'persistent_id'>,
  ): Promise<QuoteRevisionItem[]> {
    return this.bulkUpdate([{ persistent_id: persistentId, ...patch }]);
  }

  async bulkDelete(persistentIds: string[]): Promise<QuoteRevisionItem[]> {
    return this.mutate('delete', { body: { persistent_ids: persistentIds } });
  }

  async reload(): Promise<QuoteRevisionItem[]> {
    if (!this.quoteId || this.revisionNumber == null) {
      throw new Error('Revision items have not been loaded.');
    }
    return this.getQuoteRevisionItems(this.quoteId, this.revisionNumber);
  }

  async rerunDfmChecks(cadObjectId: number, materialId: number): Promise<void> {
    this.dfmRerunning.set(true);
    const items = this.itemsSubject.value;
    for (const check of flatten(items ?? []).flatMap(
      (item) => item.dfm_checks,
    )) {
      if (
        check.cad_object_id === cadObjectId &&
        check.material_id === materialId
      ) {
        check.status = DFMCheckStatus.PENDING;
      }
    }
    if (items) {
      this.itemsSubject.next([...items]);
    }
    try {
      await firstValueFrom(
        this.http.post(`/quotes-api/cad-object/${cadObjectId}/dfm-checks/`, {
          material_id: materialId,
        }),
      );
      await this.reload();
    } finally {
      this.dfmRerunning.set(false);
    }
  }

  async moveItem(
    targetPersistentId: string | null,
    position: 'before' | 'end' | 'child',
  ): Promise<QuoteRevisionItem[]> {
    const dragged = this.draggingSubject.value;
    const roots = this.itemsSubject.value;
    if (!dragged || !roots) {
      throw new Error('No revision item is being dragged.');
    }
    const all = flatten(roots);
    const target = targetPersistentId
      ? all.find((item) => item.persistent_id === targetPersistentId)
      : undefined;
    if (
      target?.persistent_id === dragged.persistent_id ||
      containsPersistentId(dragged, targetPersistentId)
    ) {
      return roots;
    }
    const newParentId =
      position === 'child'
        ? targetPersistentId
        : (target?.parent_persistent_id ?? null);
    const siblings = all
      .filter(
        (item) =>
          item.parent_persistent_id === newParentId &&
          item.persistent_id !== dragged.persistent_id,
      )
      .sort((left, right) => left.sibling_order - right.sibling_order);
    const index =
      position === 'before' && target
        ? siblings.findIndex(
            (item) => item.persistent_id === target.persistent_id,
          )
        : siblings.length;
    siblings.splice(index < 0 ? siblings.length : index, 0, dragged);
    const updates = siblings.map((item, itemIndex) => ({
      persistent_id: item.persistent_id,
      parent_persistent_id:
        item.persistent_id === dragged.persistent_id
          ? newParentId
          : item.parent_persistent_id,
      sibling_order: (itemIndex + 1) * 100,
    }));
    try {
      return await this.bulkUpdate(updates);
    } finally {
      this.dragEnd();
    }
  }

  private async mutate(
    method: 'post' | 'patch' | 'delete',
    bodyOrOptions: object,
  ): Promise<QuoteRevisionItem[]> {
    if (this.loading()) {
      throw new Error('Revision items are still loading.');
    }
    const url = this.revisionItemsUrl();
    const request =
      method === 'delete'
        ? this.http.delete<unknown>(url, bodyOrOptions)
        : method === 'post'
          ? this.http.post<unknown>(url, bodyOrOptions)
          : this.http.patch<unknown>(url, bodyOrOptions);
    const response = await firstValueFrom(request);
    const parsed = QuoteRevisionItemsMutationResponse.parse(response);
    this.itemsSubject.next(parsed.items);
    this.errorSubject.next(null);
    const revisionChanged = this.revisionNumber !== parsed.revision_number;
    this.revisionNumber = parsed.revision_number;
    if (revisionChanged) {
      this.connectDfmSocket();
    }
    await this.quoteService.getRevisions(true);
    await this.quoteService.loadRevision(parsed.revision_number);
    return parsed.items;
  }

  private revisionItemsUrl(): string {
    if (!this.quoteId || this.revisionNumber == null) {
      throw new Error('Revision items have not been loaded.');
    }
    return `/quotes-api/quote-revision-items/${this.quoteId}/${this.revisionNumber}/`;
  }

  private connectDfmSocket(): void {
    this.disconnectDfmSocket();
    if (!this.quoteId || this.revisionNumber == null) {
      return;
    }
    const url = `/ws/quote-dfm/${this.quoteId}/${this.revisionNumber}`;
    this.dfmSocketSubscription = this.realtimeSocket
      .listen({
        url,
        eventType: 'quote.dfm_updated',
        payloadKey: 'dfm_update',
        schema: z.object({
          quote_id: z.number(),
          revision_number: z.number(),
          cad_object_id: z.number(),
          material_id: z.number(),
        }),
      })
      .subscribe(() => {
        void this.reload().catch(() => undefined);
      });
    this.pricingSocketSubscription = this.realtimeSocket
      .listen({
        url,
        eventType: 'quote.pricing_updated',
        payloadKey: 'quote_pricing',
        schema: z.object({
          quote_id: z.number(),
          revision_number: z.number(),
        }),
      })
      .subscribe(() => {
        void this.refreshPricing();
      });
  }

  private disconnectDfmSocket(): void {
    this.dfmSocketSubscription?.unsubscribe();
    this.dfmSocketSubscription = undefined;
    this.pricingSocketSubscription?.unsubscribe();
    this.pricingSocketSubscription = undefined;
  }

  private async refreshPricing(): Promise<void> {
    await Promise.all([
      this.reload().catch(() => undefined),
      this.quoteService.refreshRevision().catch(() => undefined),
    ]);
  }
}

function flatten(items: QuoteRevisionItem[]): QuoteRevisionItem[] {
  return items.flatMap((item) => [item, ...flatten(item.children ?? [])]);
}

function containsPersistentId(
  item: QuoteRevisionItem,
  persistentId: string | null,
): boolean {
  return (
    persistentId != null &&
    (item.children ?? []).some(
      (child) =>
        child.persistent_id === persistentId ||
        containsPersistentId(child, persistentId),
    )
  );
}
